import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, Lightbulb } from "lucide-react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

/**
 * The single marker for content that came from a SIMILAR machine type rather than
 * the one the ticket is about.
 *
 * One rule holds the whole design together: **silence means verified, ink marks the
 * exception**. A source or fact that matched the customer's machine — or that the
 * scope engine could not judge — renders nothing at all, so the marker's absence is
 * itself the statement. Which is why this component is deliberately small (an icon,
 * no pill, no label text): on a fully on-type ticket it must add zero visual weight,
 * and on a mixed one the eye should land on the one row that carries it.
 *
 * Purely informational. Verifying takes no click; distrusting uses the fact
 * uncheck/delete and source controls that already exist.
 */
export interface MachineScopeMarkerProps {
  /** The type the REQUEST was about. Its presence is what makes a source "off-type";
   *  null/undefined renders nothing. */
  expectedLabel?: string | null;
  /** The type the source is actually about. Absent for a source covering too many
   *  types to name, which falls back to the generic wording. */
  sourceLabel?: string | null;
  /** What CROSS-CUTTING property this source gets wrong, already worded for the
   *  operator by the tenant ("Steuerungsplattform"). Independent of the two above: a
   *  source can be about the right machine type and still run a different control
   *  platform, which is why it cannot be folded into `expectedLabel` — the machine
   *  wording would then claim a type mismatch that did not happen. */
  factLabel?: string | null;
  className?: string;
}

/** Distance between the bulb and the bubble's near edge. */
const TOOLTIP_GAP = 8;
/** Kept in sync with the bubble's `max-w-[420px]`, to keep it inside the viewport
 *  without a second measure-and-reflow pass. Wide rather than tall: the sentence
 *  names two machine types and wraps to a narrow column of five or six lines at
 *  280px, which reads as a paragraph to work through instead of a label to glance
 *  at. In a panel narrower than that (the Outlook sidebar) the bubble's own
 *  `calc(100vw-16px)` cap takes over, and the clamp below centres it. */
const TOOLTIP_MAX_WIDTH = 420;
/** Below this much room above the bulb, the bubble flips underneath it. A marker on
 *  the first source card sits near the top of the panel, where "above" is offscreen. */
const FLIP_THRESHOLD = 96;

type Placement = "top" | "bottom";

/**
 * Where the bubble goes, in viewport coordinates.
 *
 * A `title` attribute was the first version of this and read as a footnote nobody
 * opened: the browser's ~1s delay is not configurable and its font size is not ours.
 * So the bubble is drawn here — portalled to `document.body` and positioned `fixed`,
 * which is what lets one component serve the source card, the fact row and the to-do
 * list without knowing anything about their overflow or stacking contexts.
 */
function useTooltipAnchor(triggerRef: React.RefObject<HTMLElement | null>) {
  const [anchor, setAnchor] = useState<{
    left: number;
    top: number;
    placement: Placement;
  } | null>(null);

  const show = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const placement: Placement = rect.top < FLIP_THRESHOLD ? "bottom" : "top";
    const half = TOOLTIP_MAX_WIDTH / 2;
    const min = TOOLTIP_GAP + half;
    const max = window.innerWidth - TOOLTIP_GAP - half;
    const centre = rect.left + rect.width / 2;
    setAnchor({
      // A viewport narrower than the bubble has no valid clamp; centring beats
      // pinning it to one edge.
      left: min > max ? window.innerWidth / 2 : Math.min(Math.max(centre, min), max),
      top: placement === "top" ? rect.top - TOOLTIP_GAP : rect.bottom + TOOLTIP_GAP,
      placement,
    });
  }, [triggerRef]);

  const hide = useCallback(() => setAnchor(null), []);

  // The anchor is a snapshot of where the bulb was, so anything that moves it while
  // the bubble is open would leave the bubble behind. Closing is the honest response:
  // the pointer has left the row that the bubble was describing.
  useEffect(() => {
    if (!anchor) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") hide();
    };
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
      window.removeEventListener("keydown", onKey);
    };
  }, [anchor, hide]);

  return { anchor, show, hide };
}

/**
 * What the marker says about a source, or null for a source that matches the customer's
 * machine. A machine-type mismatch is the stronger statement and keeps its own wording;
 * the fact only speaks when the type matched (or could not be judged), so the two never
 * compete for the one bulb.
 */
export function machineScopeSentence(
  t: TFunction,
  { expectedLabel, sourceLabel, factLabel }: Omit<MachineScopeMarkerProps, "className">,
): string | null {
  if (expectedLabel) {
    return sourceLabel
      ? t("machineScope.markerTooltip", { sourceType: sourceLabel, expectedType: expectedLabel })
      : t("machineScope.markerTooltipUnnamed", { expectedType: expectedLabel });
  }
  return factLabel ? t("machineScope.factMarkerTooltip", { factType: factLabel }) : null;
}

export function MachineScopeMarker({
  expectedLabel,
  sourceLabel,
  factLabel,
  className,
}: MachineScopeMarkerProps) {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const { anchor, show, hide } = useTooltipAnchor(triggerRef);

  const tooltip = machineScopeSentence(t, { expectedLabel, sourceLabel, factLabel });
  if (!tooltip) return null;

  return (
    <>
      <span
        ref={triggerRef}
        // No `title`: it would queue a second, slower, smaller tooltip behind this one.
        // The label carries the same sentence for screen readers, so the bubble itself
        // stays aria-hidden rather than announcing it twice.
        aria-label={tooltip}
        role="img"
        tabIndex={0}
        onPointerEnter={show}
        onPointerLeave={hide}
        onFocus={show}
        onBlur={hide}
        className={`inline-flex shrink-0 cursor-help items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primary/40${
          className ? " " + className : ""
        }`}
      >
        <Lightbulb className="h-[13px] w-[13px] text-amber-600" aria-hidden="true" />
      </span>
      {anchor &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="tooltip"
            aria-hidden="true"
            style={{
              position: "fixed",
              left: anchor.left,
              top: anchor.top,
              transform:
                anchor.placement === "top"
                  ? "translate(-50%, -100%)"
                  : "translate(-50%, 0)",
            }}
            className="pointer-events-none z-[100] max-w-[min(420px,calc(100vw-16px))] rounded-md border border-border bg-popover px-3 py-2 text-[13px] leading-relaxed text-popover-foreground shadow-lg"
          >
            {tooltip}
          </div>,
          document.body,
        )}
    </>
  );
}

/** Whether a source card carries the marker — the one predicate, so the card, the
 *  fact row and the all-similar banner cannot disagree about what "off-type" means. */
export function isMachineScopeMismatch(
  ref:
    | { scopeExpectedLabel?: string | null; scopeFactLabel?: string | null }
    | null
    | undefined,
): boolean {
  return !!ref?.scopeExpectedLabel || !!ref?.scopeFactLabel;
}


/**
 * Shown instead of per-card markers when NOTHING in a group matched the customer's
 * machine.
 *
 * With every card marked, the marker stops marking anything — it becomes a texture
 * the eye filters out, and the one thing worth saying (the answer rests on related
 * types) is the thing it stops saying. So the group states it once, calmly, and the
 * cards go quiet.
 */
export function AllSimilarMachineNote({ expectedLabel }: { expectedLabel: string }) {
  const { t } = useTranslation();
  return (
    <div className="mb-2 flex items-start gap-2 rounded-md border border-primary/20 bg-primary/5 px-2.5 py-2 text-[11px] leading-relaxed text-foreground/80">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
      <span>{t("machineScope.allSimilarNote", { expectedType: expectedLabel })}</span>
    </div>
  );
}

/**
 * The leading slot a source row puts the marker in.
 *
 * The marker used to end the title row, which reads correctly in a narrow sidebar
 * and falls apart in a wide one: the title stops early, the bulb sits at the far
 * edge, and it reads as a property of the row rather than of the source. So it leads
 * the row, where it reads as a property of the source.
 *
 * Only a marked row takes room. An unmarked row used to reserve the icon's width anyway
 * so markers shared one vertical line, but that left every row of a list with nothing
 * to mark indented by an empty 13px gap; the warning is rare enough that an aligned
 * title edge matters more than an aligned bulb column.
 */
export function MachineScopeSlot({
  expectedLabel,
  sourceLabel,
  factLabel,
}: MachineScopeMarkerProps) {
  if (!expectedLabel && !factLabel) return null;
  return (
    <span className="flex w-[13px] shrink-0 items-center justify-center">
      <MachineScopeMarker
        expectedLabel={expectedLabel}
        sourceLabel={sourceLabel}
        factLabel={factLabel}
      />
    </span>
  );
}
